import React, { useState, useEffect, useCallback } from 'react';

interface Props {
  onClose: () => void;
  onOpenSidebar: () => void;
}

const STEPS = [
  {
    selector: '.nav-sidebar .nav-item:nth-child(2) .nav-link',
    icon: '⚡',
    title: 'Terminal',
    description: 'Aquí ejecutas SQL directamente: CREATE TABLE, SELECT y más comandos.',
    color: '#28a745',
  },
  {
    selector: '.nav-sidebar .nav-item:nth-child(3) .nav-link',
    icon: '📝',
    title: 'Form Wizard',
    description: 'Formulario guiado sin SQL. Crea tu CRUD de forma visual sin escribir código.',
    color: '#ffc107',
  },
  {
    selector: '.nav-sidebar .nav-item:nth-child(4) .nav-link',
    icon: '🌐',
    title: 'Remote Connection',
    description: 'Conecta OmniCrud a una base de datos remota de forma segura y sencilla.',
    color: '#ff0730',
  },
];

interface Rect { top: number; left: number; width: number; height: number; }

function getRect(selector: string): Rect | null {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const PAD = 6;
const CONTENT_SELECTOR = '.content-wrapper';

const Onboarding: React.FC<Props> = ({ onClose, onOpenSidebar }) => {
  const [step, setStep]             = useState(0);
  const [rect, setRect]             = useState<Rect | null>(null);
  const [visible, setVisible]       = useState(true);
  const [tooltipKey, setTooltipKey] = useState(0);
  const [ready, setReady]           = useState(false);

  const isMobile = window.innerWidth <= 768;

  const current = STEPS[step];
  const isLast  = step === STEPS.length - 1;

  useEffect(() => {
    const content = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (content) content.classList.add('ob-blurred');

    if (isMobile) {
      onOpenSidebar();
      setTimeout(() => setReady(true), 380);
    } else {
      setReady(true);
    }

    return () => {
      if (content) content.classList.remove('ob-blurred');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRect = useCallback(() => {
    if (!ready) return;
    setRect(getRect(STEPS[step].selector));
  }, [step, ready]);

  useEffect(() => {
    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [updateRect]);

  const handleClose = () => {
    setVisible(false);
    const content = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (content) content.classList.remove('ob-blurred');
    setTimeout(() => onClose(), 280);
  };

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setStep(s => s + 1);
      setTooltipKey(k => k + 1);
    }
  };

  const tooltipStyle = (): React.CSSProperties => {
    if (!rect) return { opacity: 0 };

    if (isMobile) {
      const tooltipTop = rect.top + rect.height + PAD + 14;
      return {
        top:   tooltipTop,
        left:  16,
        width: 'calc(100vw - 32px)',
      };
    }
    return {
      top:   rect.top + rect.height / 2 - 100,
      left:  rect.left + rect.width + PAD + 14,
      width: 270,
    };
  };

  if (!ready) return null;

  return (
    <>
      <div
        className={`ob-overlay${visible ? ' ob-enter' : ' ob-exit'}`}
        onClick={handleClose}
      />

      {rect && (
        <div
          className="ob-spotlight"
          style={{
            top:    rect.top    - PAD,
            left:   rect.left   - PAD,
            width:  rect.width  + PAD * 2,
            height: rect.height + PAD * 2,
            '--accent': current.color,
          } as React.CSSProperties}
        />
      )}

      {rect && (
        <div
          key={tooltipKey}
          className={`ob-tooltip${!isMobile ? ' ob-arrow-left' : ' ob-arrow-top'}${visible ? ' ob-enter' : ' ob-exit'}`}
          style={tooltipStyle()}
        >
          <div className="ob-header">
            <span className="ob-icon">{current.icon}</span>
            <span className="ob-title" style={{ color: current.color }}>
              {current.title}
            </span>
          </div>

          <p className="ob-desc">{current.description}</p>

          <div className="ob-meta">
            <div className="ob-dots">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`ob-dot${i === step ? ' active' : i < step ? ' done' : ''}`}
                  style={i === step ? { background: current.color, transform: 'scale(1.35)' } : {}}
                />
              ))}
            </div>
            <span className="ob-counter">{step + 1} / {STEPS.length}</span>
          </div>

          <div className="ob-actions">
            <button className="ob-skip" onClick={handleClose}>Saltar</button>
            <button
              className="ob-next"
              style={{ background: current.color }}
              onClick={handleNext}
            >
              {isLast ? '¡Entendido! ✓' : 'Siguiente →'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Onboarding;